interface EnhancedRequestOptions extends RequestInit {
  params?: Record<string, string>;
  isMultipart?: boolean;
  withAuth?: boolean;
}

const getToken = () => {
  return typeof window !== "undefined"
    ? localStorage.getItem("token") || ""
    : ""; // or cookies if SSR
};

const buildUrlWithParams = (
  url: string,
  params?: Record<string, string>
): string => {
  if (!params) return url;
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined) queryParams.append(key, v);
      });
    } else if (value !== undefined) {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};

export const apiClient = {
  get: async (
    url: string,
    params?: Record<string, string>,
    options: EnhancedRequestOptions = {}
  ) => {
    const fullUrl = buildUrlWithParams(url, params);
    return fetchWithHandling(fullUrl, { method: "GET", ...options });
  },

  getWithBody: async (
    url: string,
    body: any,
    params?: Record<string, string>,
    options: EnhancedRequestOptions = {}
  ) => {
    // Convert body to query parameters
    const bodyParams = new URLSearchParams();
    if (body) {
      const encodedData = encodeURIComponent(JSON.stringify(body));
      bodyParams.append("parameters", encodedData);
    }
    
    // Merge with existing params
    const allParams = { ...params };
    bodyParams.forEach((value, key) => {
      allParams[key] = value;
    });
    
    const fullUrl = buildUrlWithParams(url, allParams);
    const response = await fetch(fullUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`API Error ${response.status}: ${fullUrl} ${message}`);
    }

    return response.headers.get("Content-Type")?.includes("application/json")
      ? response.json()
      : response.text();
  },

  post: async (
    url: string,
    body: any,
    options: EnhancedRequestOptions = {}
  ) => {
    return fetchWithHandling(url, {
      method: "POST",
      body,
      ...options,
    });
  },

  put: async (url: string, body: any, options: EnhancedRequestOptions = {}) => {
    return fetchWithHandling(url, {
      method: "PUT",
      body,
      ...options,
    });
  },

  delete: async (url: string, options: EnhancedRequestOptions = {}) => {
    return fetchWithHandling(url, {
      method: "DELETE",
      ...options,
    });
  },
};

const fetchWithHandling = async (
  url: string,
  options: EnhancedRequestOptions
) => {
  const headers: Record<string, string> = {};

  // Merge any headers passed in through options
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, options.headers);
    }
  }

  if (!options.isMultipart) {
    headers["Content-Type"] = "application/json";
  }

  if (options.withAuth) {
    headers["Authorization"] = `Bearer ${getToken()}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
    body: options.isMultipart ? options.body : JSON.stringify(options.body),
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`API Error ${response.status}: ${url} ${message}`);
  }

  return response.headers.get("Content-Type")?.includes("application/json")
    ? response?.json()
    : response.text();
};
